import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import ManagerApprovalDialog from "./ManagerApprovalDialog";

/**
 * Voids the whole invoice (input-error corrections). Manager Approval
 * popup is displayed when the operator only holds
 * `location.orders_pos.edit` — the payload is replayed against
 * `/pos-approvals/inline` when a manager enters the one-time code emailed
 * to them.
 */
export default function VoidDialog({ open, onOpenChange, order, branch, onVoided }) {
  const { staff } = useAuth();
  const canAuthorise = hasPermission(staff, "location.orders_pos.delete");
  const [preview, setPreview] = useState(null);
  const [returns, setReturns] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // If the operator lacks `.delete` we pop the Manager Approval popup
  // instead of running the void directly. `approvalPayload` is the same
  // body we would have POSTed to `/orders/{oid}/void`.
  const [approvalPayload, setApprovalPayload] = useState(null);

  useEffect(() => {
    if (!open || !order) { setPreview(null); setReason(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await staffApi.get(`/orders/${order.id}/void-preview`);
        if (cancelled) return;
        // If the branch's Start-of-Day has moved past this order's business
        // day, the void window has closed — bail out immediately so the
        // operator lands on the Refund button instead.
        if (r.data && r.data.can_void === false) {
          toast.error("This order's business day has closed — please use Refund.");
          onOpenChange(false);
          return;
        }
        setPreview(r.data);
        // Default: every product opted-IN for return so the operator's happy
        // path is a single click.
        const init = {};
        for (const p of (r.data.products || [])) init[p.product_id] = true;
        setReturns(init);
      } catch (err) {
        toast.error(formatApiError(err));
        onOpenChange(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order, onOpenChange]);

  const toggle = (pid) => setReturns((s) => ({ ...s, [pid]: !s[pid] }));

  const doVoid = async () => {
    if (busy) return;
    const payload = { product_returns: returns, reason: reason.trim() };
    // Edit-only user: hand off to the Manager Approval popup instead of
    // calling `/void` directly (backend would reject with 403 anyway).
    if (!canAuthorise) {
      setApprovalPayload(payload);
      return;
    }
    setBusy(true);
    try {
      await staffApi.post(`/orders/${order.id}/void`, payload);
      onVoided();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open && !approvalPayload} onOpenChange={onOpenChange}>
        <DialogContent data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle>Void this order?</DialogTitle>
            <DialogDescription>
              Void cancels the whole invoice for input-error corrections (wrong payment method, wrong quantity, etc.) — it&apos;s only allowed while the order&apos;s business day is still open on this branch (until the next Start-of-Day). The linked appointment stays on the calendar as unpaid.
            </DialogDescription>
          </DialogHeader>

          {preview && preview.products.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm">Would you like to put back the product inventory record?</p>
              <div className="rounded-md border border-border divide-y divide-border">
                {preview.products.map((p) => (
                  <label key={p.product_id} className="flex items-center justify-between p-3 gap-3 cursor-pointer hover:bg-muted/40" data-testid={`void-return-row-${p.product_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-muted-foreground">Used: {p.quantity} · Current stock: {p.current_stock}</div>
                    </div>
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-rose-600"
                      checked={!!returns[p.product_id]}
                      onChange={() => toggle(p.product_id)}
                      data-testid={`void-return-toggle-${p.product_id}`}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : preview ? (
            <p className="text-sm text-muted-foreground">This order has no product lines — nothing to return to inventory.</p>
          ) : (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}

          <div className="space-y-1.5 pt-2">
            <Label htmlFor="void-reason-input" className="text-xs uppercase tracking-wider text-muted-foreground">Reason for void (audit trail)</Label>
            <Input
              id="void-reason-input"
              data-testid="void-reason-input"
              placeholder="e.g. Wrong service selected, duplicate charge…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="void-cancel-btn">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={doVoid} disabled={!preview || busy} data-testid="void-confirm-btn">
              {busy ? "Voiding…" : (canAuthorise ? "Void order" : "Request void…")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manager approval popup — only reached when the operator lacks
          `location.orders_pos.delete`. Rendered as a SIBLING of the
          void dialog (not nested) so Radix's Dialog primitive only ever
          shows one panel at a time. */}
      <ManagerApprovalDialog
        open={!!approvalPayload}
        onOpenChange={(o) => { if (!o) setApprovalPayload(null); }}
        orderId={order?.id}
        kind="void"
        payload={approvalPayload || {}}
        actionLabel="Void order"
        onApproved={() => { setApprovalPayload(null); onVoided(); }}
        onQueued={() => { setApprovalPayload(null); onOpenChange(false); }}
      />
    </>
  );
}
